import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import api from '../lib/api';
import { h, writePrintHtml } from '../lib/printHelpers';

const OperationsHub = ({ user }) => {
  const [activeTab, setActiveTab] = useState('po-variance');
  const tabs = [
    { key: 'po-variance', label: 'PO Variance', color: 'blue' },
    { key: 'po-aging', label: 'PO Aging', color: 'orange' },
    { key: 'po-recon', label: 'PO Reconciliation', color: 'purple' },
    { key: 'vendor-score', label: 'Vendor Scorecard', color: 'teal' },
    { key: 'so-variance', label: 'SO Variance', color: 'indigo' },
    { key: 'so-aging', label: 'SO Aging', color: 'amber' },
    { key: 'so-recon', label: 'SO Reconciliation', color: 'rose' },
    { key: 'customer-score', label: 'Customer Scorecard', color: 'emerald' },
    { key: 'reorder', label: 'Reorder Alerts', color: 'red' },
    { key: 'labels', label: 'QR Labels', color: 'violet' },
  ];

  return (
    <div className="p-6 space-y-5 animate-fadeIn" data-testid="operations-hub">
      <div>
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Operations Hub</h1>
        <p className="text-gray-600 mt-1">Variance reports, aging analysis, reconciliation, scorecards, and stock alerts</p>
      </div>
      <div className="flex gap-1 flex-wrap border-b pb-0.5">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === t.key ? `border-${t.color}-600 text-${t.color}-700 bg-${t.color}-50/50` : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid={`tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>
      {activeTab === 'po-variance' && <POVarianceTab />}
      {activeTab === 'po-aging' && <AgingTab type="po" />}
      {activeTab === 'po-recon' && <ReconciliationTab type="po" />}
      {activeTab === 'vendor-score' && <ScorecardTab type="vendor" />}
      {activeTab === 'so-variance' && <SOVarianceTab />}
      {activeTab === 'so-aging' && <AgingTab type="so" />}
      {activeTab === 'so-recon' && <ReconciliationTab type="so" />}
      {activeTab === 'customer-score' && <ScorecardTab type="customer" />}
      {activeTab === 'reorder' && <ReorderAlertsTab user={user} />}
      {activeTab === 'labels' && <BarcodeLabelsTab />}
    </div>
  );
};

// ===== SHARED HELPERS =====
const Loader = ({ color = 'blue' }) => (
  <div className="flex items-center justify-center h-32"><div className={`animate-spin rounded-full h-8 w-8 border-b-2 border-${color}-600`} /></div>
);
const SummaryCard = ({ label, value, color, active, onClick }) => (
  <Card className={`${color} ${active ? 'ring-2 ring-offset-1' : ''} ${onClick ? 'cursor-pointer hover:shadow-md' : ''} transition-all`} onClick={onClick}>
    <CardContent className="p-3 text-center">
      <p className="text-xs opacity-70">{label}</p>
      <p className="text-xl font-bold">{typeof value === 'number' ? value.toLocaleString('en-IN') : value}</p>
    </CardContent>
  </Card>
);
const statusColor = (s) => {
  const map = {
    matched: 'text-green-700 bg-green-50', shortfall: 'text-red-700 bg-red-50', excess: 'text-blue-700 bg-blue-50',
    fulfilled: 'text-green-700 bg-green-50', partial: 'text-amber-700 bg-amber-50', pending: 'text-red-700 bg-red-50',
    no_entry: 'text-red-700 bg-red-50', under_billed: 'text-amber-700 bg-amber-50', over_billed: 'text-blue-700 bg-blue-50',
    under_received: 'text-amber-700 bg-amber-50', over_received: 'text-blue-700 bg-blue-50',
  };
  return map[s] || 'text-gray-700 bg-gray-50';
};
const gradeColor = (g) => g === 'A' ? 'bg-green-100 text-green-800' : g === 'B' ? 'bg-blue-100 text-blue-800' : g === 'C' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800';

// ===== PO VARIANCE TAB =====
const POVarianceTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { api.get('/reports/po-variance').then(r => setData(r.data)).catch(() => toast.error('Failed')).finally(() => setLoading(false)); }, []);
  if (loading) return <Loader />;
  if (!data) return null;
  const { rows, summary } = data;
  const filtered = filter === 'all' ? rows : rows.filter(r => r.status === filter);

  return (
    <div className="space-y-4" data-testid="po-variance-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label="Total Ordered" value={summary.total_ordered} color="bg-gray-50" />
        <SummaryCard label="Total Received" value={summary.total_received} color="bg-gray-50" />
        <SummaryCard label="Matched" value={summary.matched} color="bg-green-50" active={filter === 'matched'} onClick={() => setFilter(filter === 'matched' ? 'all' : 'matched')} />
        <SummaryCard label="Shortfall" value={summary.shortfall} color="bg-red-50" active={filter === 'shortfall'} onClick={() => setFilter(filter === 'shortfall' ? 'all' : 'shortfall')} />
        <SummaryCard label="Excess" value={summary.excess} color="bg-blue-50" active={filter === 'excess'} onClick={() => setFilter(filter === 'excess' ? 'all' : 'excess')} />
      </div>
      {filtered.length > 0 && filtered.some(r => r.variance !== 0) && (
        <Card><CardContent className="p-4">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={filtered.filter(r => r.variance !== 0).slice(0, 15)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="design_code" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => `${v} pcs`} />
              <Bar dataKey="variance" name="Variance">{filtered.filter(r => r.variance !== 0).slice(0, 15).map((r, i) => <Cell key={i} fill={r.variance > 0 ? '#3b82f6' : '#ef4444'} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent></Card>
      )}
      <VarianceTable rows={filtered} columns={[
        { key: 'po_number', label: 'PO #' }, { key: 'vendor', label: 'Vendor' }, { key: 'design_code', label: 'Design' },
        { key: 'qty_ordered', label: 'Ordered', align: 'right' }, { key: 'qty_received', label: 'Received', align: 'right' },
        { key: 'variance', label: 'Variance', align: 'right', color: true }, { key: 'status', label: 'Status', badge: true },
      ]} />
    </div>
  );
};

// ===== SO VARIANCE TAB =====
const SOVarianceTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { api.get('/reports/so-variance').then(r => setData(r.data)).catch(() => toast.error('Failed')).finally(() => setLoading(false)); }, []);
  if (loading) return <Loader color="indigo" />;
  if (!data) return null;
  const { rows, alerts, summary } = data;
  const filtered = filter === 'all' ? rows : rows.filter(r => r.status === filter);

  return (
    <div className="space-y-4" data-testid="so-variance-tab">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <SummaryCard label="Total Ordered" value={summary.total_ordered} color="bg-gray-50" />
        <SummaryCard label="Total Dispatched" value={summary.total_dispatched} color="bg-gray-50" />
        <SummaryCard label="Total Pending" value={summary.total_pending} color="bg-orange-50" />
        <SummaryCard label="Fulfilled" value={summary.fulfilled} color="bg-green-50" active={filter === 'fulfilled'} onClick={() => setFilter(filter === 'fulfilled' ? 'all' : 'fulfilled')} />
        <SummaryCard label="Partial" value={summary.partial} color="bg-amber-50" active={filter === 'partial'} onClick={() => setFilter(filter === 'partial' ? 'all' : 'partial')} />
        <SummaryCard label="Pending" value={summary.pending_count} color="bg-red-50" active={filter === 'pending'} onClick={() => setFilter(filter === 'pending' ? 'all' : 'pending')} />
      </div>
      {summary.overall_fulfillment_pct > 0 && (
        <Card className="bg-indigo-50 border-indigo-200"><CardContent className="p-3 flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-indigo-600 text-white flex items-center justify-center text-lg font-bold">{summary.overall_fulfillment_pct}%</div>
          <div><p className="font-semibold text-indigo-900">Overall Fulfillment Rate</p><p className="text-xs text-indigo-700">Across all active sales orders</p></div>
        </CardContent></Card>
      )}
      {alerts.length > 0 && (
        <Card className="border-red-200 bg-red-50/30"><CardHeader className="pb-2"><CardTitle className="text-sm text-red-800">Top Variance Alerts ({alerts.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto"><table className="w-full text-xs">
              <thead className="bg-red-50"><tr><th className="px-3 py-2 text-left">SO #</th><th className="px-3 py-2 text-left">Customer</th><th className="px-3 py-2 text-left">Design</th><th className="px-3 py-2 text-right">Pending Qty</th><th className="px-3 py-2 text-right">Fulfillment</th></tr></thead>
              <tbody>{alerts.map((a, i) => (
                <tr key={i} className="border-b"><td className="px-3 py-1.5 font-medium">{a.so_number}</td><td className="px-3 py-1.5">{a.customer}</td><td className="px-3 py-1.5">{a.design_code}</td>
                  <td className="px-3 py-1.5 text-right font-bold text-red-600">{a.qty_pending}</td><td className="px-3 py-1.5 text-right">{a.fulfillment_pct}%</td></tr>
              ))}</tbody>
            </table></div>
          </CardContent>
        </Card>
      )}
      {filtered.length > 0 && filtered.some(r => r.variance !== 0) && (
        <Card><CardContent className="p-4">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={filtered.filter(r => r.variance !== 0).slice(0, 15)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="design_code" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => `${v} pcs`} />
              <Bar dataKey="variance" name="Variance">{filtered.filter(r => r.variance !== 0).slice(0, 15).map((r, i) => <Cell key={i} fill={r.variance > 0 ? '#6366f1' : '#ef4444'} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent></Card>
      )}
      <VarianceTable rows={filtered} columns={[
        { key: 'so_number', label: 'SO #' }, { key: 'customer', label: 'Customer' }, { key: 'design_code', label: 'Design' },
        { key: 'qty_ordered', label: 'Ordered', align: 'right' }, { key: 'qty_dispatched', label: 'Dispatched', align: 'right' },
        { key: 'qty_pending', label: 'Pending', align: 'right', highlight: true }, { key: 'fulfillment_pct', label: 'Fulfillment', align: 'right', suffix: '%' },
        { key: 'status', label: 'Status', badge: true },
      ]} emptyMsg="No active sales orders with variance data" />
    </div>
  );
};

// ===== GENERIC VARIANCE TABLE =====
const VarianceTable = ({ rows, columns, emptyMsg = 'No data' }) => (
  <Card><CardContent className="p-0"><div className="overflow-x-auto"><table className="w-full text-sm">
    <thead className="bg-gray-50 border-b"><tr>
      {columns.map(c => <th key={c.key} className={`${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left'} px-4 py-3 font-semibold text-gray-600 text-xs`}>{c.label}</th>)}
    </tr></thead>
    <tbody>
      {rows.length === 0 ? <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500">{emptyMsg}</td></tr> :
        rows.map((r, i) => (
        <tr key={i} className="border-b hover:bg-gray-50/80">
          {columns.map(c => {
            const val = r[c.key];
            if (c.badge) return <td key={c.key} className="px-4 py-2 text-center"><span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${statusColor(val)}`}>{String(val).toUpperCase().replace('_', ' ')}</span></td>;
            if (c.color) return <td key={c.key} className={`px-4 py-2 text-right font-bold ${val > 0 ? 'text-blue-600' : val < 0 ? 'text-red-600' : 'text-green-600'}`}>{val > 0 ? '+' : ''}{val}{c.suffix || ''}</td>;
            if (c.highlight) return <td key={c.key} className={`px-4 py-2 text-right font-bold ${val > 0 ? 'text-red-600' : 'text-green-600'}`}>{val}{c.suffix || ''}</td>;
            return <td key={c.key} className={`px-4 py-2 ${c.align === 'right' ? 'text-right' : ''} ${c.key.includes('number') ? 'text-xs font-medium' : ''}`}>{val}{c.suffix || ''}</td>;
          })}
        </tr>
      ))}
    </tbody>
  </table></div></CardContent></Card>
);

// ===== AGING TAB (PO + SO) =====
const AgingTab = ({ type }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const endpoint = type === 'po' ? '/reports/po-aging' : '/reports/so-aging';
  const entityLabel = type === 'po' ? 'PO' : 'SO';
  const counterpartyLabel = type === 'po' ? 'Vendor' : 'Customer';

  useEffect(() => { api.get(endpoint).then(r => setData(r.data)).catch(() => toast.error('Failed')).finally(() => setLoading(false)); }, []);
  if (loading) return <Loader color="orange" />;
  if (!data) return null;

  const bucketOrder = ["0-7 days", "8-15 days", "16-30 days", "30+ days"];
  const bucketColors = { "0-7 days": "#22c55e", "8-15 days": "#f59e0b", "16-30 days": "#f97316", "30+ days": "#ef4444" };
  const chartData = bucketOrder.filter(b => data.buckets[b]).map(b => ({ name: b, count: data.buckets[b].count, pending: data.buckets[b].pending_qty }));

  return (
    <div className="space-y-4" data-testid={`${type}-aging-tab`}>
      <Card className="bg-amber-50 border-amber-200"><CardContent className="p-4 flex items-center gap-3">
        <div className="w-14 h-14 rounded-full bg-amber-500 text-white flex items-center justify-center text-lg font-bold">{data.total_pending_orders}</div>
        <div><p className="font-bold text-amber-900">Pending {entityLabel}s with Outstanding Items</p><p className="text-xs text-amber-700">Orders that haven't been fully {type === 'po' ? 'received' : 'dispatched'}</p></div>
      </CardContent></Card>

      {chartData.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {bucketOrder.map(b => data.buckets[b] && (
            <Card key={b} style={{ borderLeftColor: bucketColors[b], borderLeftWidth: 4 }}>
              <CardContent className="p-3 text-center">
                <p className="text-xs text-gray-500">{b}</p>
                <p className="text-xl font-bold">{data.buckets[b].count}</p>
                <p className="text-[10px] text-gray-400">{data.buckets[b].pending_qty} pcs pending</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {chartData.length > 0 && (
        <Card><CardContent className="p-4">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
              <Tooltip /><Bar dataKey="pending" name="Pending Qty">{chartData.map((d, i) => <Cell key={i} fill={bucketColors[d.name]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent></Card>
      )}

      <Card><CardContent className="p-0"><div className="overflow-x-auto"><table className="w-full text-sm">
        <thead className="bg-gray-50 border-b"><tr>
          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">{entityLabel} #</th>
          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">{counterpartyLabel}</th>
          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Date</th>
          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600">Ordered</th>
          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600">{type === 'po' ? 'Received' : 'Dispatched'}</th>
          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600">Pending</th>
          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600">Age (Days)</th>
          <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600">Bucket</th>
        </tr></thead>
        <tbody>
          {data.rows.length === 0 ? <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No pending {entityLabel.toLowerCase()}s</td></tr> :
            data.rows.map((r, i) => (
            <tr key={i} className="border-b hover:bg-gray-50/80">
              <td className="px-4 py-2 text-xs font-medium">{r[`${type}_number`]}</td>
              <td className="px-4 py-2 text-xs">{r[type === 'po' ? 'vendor' : 'customer']}</td>
              <td className="px-4 py-2 text-xs">{r[`${type}_date`]}</td>
              <td className="px-4 py-2 text-right">{r.total_ordered}</td>
              <td className="px-4 py-2 text-right">{r[type === 'po' ? 'total_received' : 'total_dispatched']}</td>
              <td className="px-4 py-2 text-right font-bold text-red-600">{r.pending_qty}</td>
              <td className="px-4 py-2 text-right font-semibold" style={{ color: r.age_days > 30 ? '#ef4444' : r.age_days > 15 ? '#f97316' : '#22c55e' }}>{r.age_days}</td>
              <td className="px-4 py-2 text-center"><span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ backgroundColor: bucketColors[r.aging_bucket] + '20', color: bucketColors[r.aging_bucket] }}>{r.aging_bucket}</span></td>
            </tr>
          ))}
        </tbody>
      </table></div></CardContent></Card>
    </div>
  );
};

// ===== RECONCILIATION TAB (PO-PE + SO-SE) =====
const ReconciliationTab = ({ type }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const endpoint = type === 'po' ? '/reports/po-pe-reconciliation' : '/reports/so-se-reconciliation';
  const orderLabel = type === 'po' ? 'PO' : 'SO';
  const entryLabel = type === 'po' ? 'PE (GRN)' : 'SE (Invoice)';
  const counterpartyLabel = type === 'po' ? 'Vendor' : 'Customer';

  useEffect(() => { api.get(endpoint).then(r => setData(r.data)).catch(() => toast.error('Failed')).finally(() => setLoading(false)); }, []);
  if (loading) return <Loader color="purple" />;
  if (!data) return null;
  const { rows, summary, unlinked_entries } = data;
  const filtered = filter === 'all' ? rows : rows.filter(r => r.reconciliation_status === filter);

  const statusLabels = type === 'po'
    ? { no_entry: 'No GRN', under_received: 'Under Received', over_received: 'Over Received', matched: 'Matched' }
    : { no_entry: 'No Invoice', under_billed: 'Under Billed', over_billed: 'Over Billed', matched: 'Matched' };
  const statusKeys = Object.keys(statusLabels);

  return (
    <div className="space-y-4" data-testid={`${type}-recon-tab`}>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label={`Total ${orderLabel}s`} value={summary[`total_${type === 'po' ? 'pos' : 'sos'}`]} color="bg-gray-50" />
        {statusKeys.map(k => (
          <SummaryCard key={k} label={statusLabels[k]} value={summary[k]} color={k === 'matched' ? 'bg-green-50' : k === 'no_entry' ? 'bg-red-50' : 'bg-amber-50'}
            active={filter === k} onClick={() => setFilter(filter === k ? 'all' : k)} />
        ))}
      </div>

      {(summary[type === 'po' ? 'unlinked_pes' : 'unlinked_ses'] || 0) > 0 && (
        <Card className="border-amber-200 bg-amber-50/50"><CardContent className="p-3">
          <p className="text-sm font-medium text-amber-800">
            {summary[type === 'po' ? 'unlinked_pes' : 'unlinked_ses']} unlinked {entryLabel.toLowerCase()}s found — entries not tied to any {orderLabel.toLowerCase()}
          </p>
        </CardContent></Card>
      )}

      <Card><CardContent className="p-0"><div className="overflow-x-auto"><table className="w-full text-sm">
        <thead className="bg-gray-50 border-b"><tr>
          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">{orderLabel} #</th>
          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">{counterpartyLabel}</th>
          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600">{orderLabel} Qty</th>
          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600">{entryLabel.split(' ')[0]} Qty</th>
          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600">Qty Gap</th>
          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600">{orderLabel} Amount</th>
          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600">{entryLabel.split(' ')[0]} Amount</th>
          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600">Amount Gap</th>
          <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600">Status</th>
        </tr></thead>
        <tbody>
          {filtered.length === 0 ? <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">No data</td></tr> :
            filtered.map((r, i) => (
            <tr key={i} className="border-b hover:bg-gray-50/80">
              <td className="px-4 py-2 text-xs font-medium">{r[`${type}_number`] || r[`${type === 'po' ? 'po' : 'so'}_number`]}</td>
              <td className="px-4 py-2 text-xs">{r[type === 'po' ? 'vendor' : 'customer']}</td>
              <td className="px-4 py-2 text-right">{r[`${type}_qty`] || r[`${type === 'po' ? 'po' : 'so'}_qty`]}</td>
              <td className="px-4 py-2 text-right">{r[type === 'po' ? 'pe_qty' : 'se_qty']}</td>
              <td className={`px-4 py-2 text-right font-bold ${r.qty_gap > 0 ? 'text-blue-600' : r.qty_gap < 0 ? 'text-red-600' : 'text-green-600'}`}>{r.qty_gap > 0 ? '+' : ''}{r.qty_gap}</td>
              <td className="px-4 py-2 text-right text-xs">₹{(r[`${type === 'po' ? 'po' : 'so'}_amount`] || 0).toLocaleString('en-IN')}</td>
              <td className="px-4 py-2 text-right text-xs">₹{(r[type === 'po' ? 'pe_amount' : 'se_amount'] || 0).toLocaleString('en-IN')}</td>
              <td className={`px-4 py-2 text-right font-bold text-xs ${r.amount_gap > 0 ? 'text-blue-600' : r.amount_gap < 0 ? 'text-red-600' : 'text-green-600'}`}>{r.amount_gap > 0 ? '+' : ''}₹{r.amount_gap?.toLocaleString('en-IN')}</td>
              <td className="px-4 py-2 text-center"><span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${statusColor(r.reconciliation_status)}`}>{(statusLabels[r.reconciliation_status] || r.reconciliation_status).toUpperCase()}</span></td>
            </tr>
          ))}
        </tbody>
      </table></div></CardContent></Card>

      {unlinked_entries.length > 0 && (
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm text-amber-800">Unlinked {entryLabel}s</CardTitle></CardHeader>
          <CardContent className="p-0"><div className="overflow-x-auto"><table className="w-full text-xs">
            <thead className="bg-amber-50"><tr>
              <th className="text-left px-3 py-2">{entryLabel.split(' ')[0]} #</th>
              <th className="text-left px-3 py-2">{counterpartyLabel}</th>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-right px-3 py-2">Qty</th>
              <th className="text-right px-3 py-2">Amount</th>
            </tr></thead>
            <tbody>{unlinked_entries.map((e, i) => (
              <tr key={i} className="border-b">
                <td className="px-3 py-1.5 font-medium">{e[type === 'po' ? 'grn_number' : 'se_number']}</td>
                <td className="px-3 py-1.5">{e[type === 'po' ? 'vendor' : 'customer']}</td>
                <td className="px-3 py-1.5">{e[type === 'po' ? 'entry_date' : 'se_date']}</td>
                <td className="px-3 py-1.5 text-right">{e[type === 'po' ? 'pe_qty' : 'se_qty']}</td>
                <td className="px-3 py-1.5 text-right">₹{(e[type === 'po' ? 'pe_amount' : 'se_amount'] || 0).toLocaleString('en-IN')}</td>
              </tr>
            ))}</tbody>
          </table></div></CardContent>
        </Card>
      )}
    </div>
  );
};

// ===== SCORECARD TAB (Customer + Vendor) =====
const ScorecardTab = ({ type }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const endpoint = type === 'vendor' ? '/reports/vendor-fulfillment-scorecard' : '/reports/customer-fulfillment-scorecard';
  const label = type === 'vendor' ? 'Vendor' : 'Customer';

  useEffect(() => { api.get(endpoint).then(r => setData(r.data)).catch(() => toast.error('Failed')).finally(() => setLoading(false)); }, []);
  if (loading) return <Loader color="teal" />;
  if (!data) return null;

  const grades = { A: 0, B: 0, C: 0, D: 0 };
  data.forEach(d => { if (grades[d.grade] !== undefined) grades[d.grade]++; });

  return (
    <div className="space-y-4" data-testid={`${type}-scorecard-tab`}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(grades).map(([g, count]) => (
          <Card key={g} className={gradeColor(g).replace('text-', 'bg-').split(' ')[0] + '/30'}>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-gray-500">Grade {g}</p>
              <p className="text-2xl font-bold">{count}</p>
              <p className="text-[10px] text-gray-400">{g === 'A' ? '90%+' : g === 'B' ? '70-89%' : g === 'C' ? '50-69%' : '<50%'} fulfillment</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card><CardContent className="p-0"><div className="overflow-x-auto"><table className="w-full text-sm">
        <thead className="bg-gray-50 border-b"><tr>
          <th className="text-center px-3 py-3 text-xs font-semibold text-gray-600">Grade</th>
          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">{label}</th>
          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600">Total {type === 'vendor' ? 'POs' : 'Orders'}</th>
          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600">Fulfilled</th>
          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600">Partial</th>
          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600">Qty Ordered</th>
          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600">Qty {type === 'vendor' ? 'Received' : 'Dispatched'}</th>
          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600">Pending</th>
          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600">Fulfillment %</th>
          {type === 'vendor' && <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600">Defect %</th>}
        </tr></thead>
        <tbody>
          {data.length === 0 ? <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-500">No {label.toLowerCase()} data</td></tr> :
            data.map((d, i) => (
            <tr key={i} className="border-b hover:bg-gray-50/80">
              <td className="px-3 py-2 text-center"><Badge className={`${gradeColor(d.grade)} font-bold`}>{d.grade}</Badge></td>
              <td className="px-4 py-2 font-medium">{d[`${type}_name`]}</td>
              <td className="px-4 py-2 text-right">{d[type === 'vendor' ? 'total_pos' : 'total_orders']}</td>
              <td className="px-4 py-2 text-right text-green-600 font-medium">{d[type === 'vendor' ? 'fulfilled_pos' : 'fulfilled_orders']}</td>
              <td className="px-4 py-2 text-right text-amber-600">{d[type === 'vendor' ? 'partial_pos' : 'partial_orders']}</td>
              <td className="px-4 py-2 text-right">{d.total_qty_ordered}</td>
              <td className="px-4 py-2 text-right">{d[type === 'vendor' ? 'total_qty_received' : 'total_qty_dispatched']}</td>
              <td className="px-4 py-2 text-right font-bold text-red-600">{d.pending_qty}</td>
              <td className="px-4 py-2 text-right font-bold" style={{ color: d.fulfillment_rate_pct >= 90 ? '#059669' : d.fulfillment_rate_pct >= 70 ? '#d97706' : '#dc2626' }}>{d.fulfillment_rate_pct}%</td>
              {type === 'vendor' && <td className="px-4 py-2 text-right">{d.defect_pct}%</td>}
            </tr>
          ))}
        </tbody>
      </table></div></CardContent></Card>
    </div>
  );
};

// ===== REORDER ALERTS TAB =====
const ReorderAlertsTab = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingMinStock, setEditingMinStock] = useState(null);
  const [minStockValue, setMinStockValue] = useState(10);

  const load = () => api.get('/products/reorder-alerts').then(r => setData(r.data)).catch(() => toast.error('Failed')).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const setMinStock = async (productId) => {
    try { await api.put(`/products/${productId}/min-stock`, { min_stock_level: minStockValue }); toast.success(`Min stock updated`); setEditingMinStock(null); load(); } catch { toast.error('Failed'); }
  };

  if (loading) return <Loader color="red" />;
  if (!data) return null;
  const priorityColor = (p) => p === 'critical' ? 'text-red-700 bg-red-100 border-red-200' : p === 'high' ? 'text-orange-700 bg-orange-100 border-orange-200' : 'text-yellow-700 bg-yellow-100 border-yellow-200';

  return (
    <div className="space-y-4" data-testid="reorder-alerts-tab">
      <Card className={data.total > 0 ? 'border-red-200 bg-red-50/30' : 'border-green-200 bg-green-50/30'}>
        <CardContent className="p-4 flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${data.total > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{data.total}</div>
          <div><p className="font-bold text-lg">{data.total > 0 ? `${data.total} Products Below Minimum` : 'All Products Adequately Stocked'}</p></div>
        </CardContent>
      </Card>
      {data.alerts.length > 0 && (
        <Card><CardContent className="p-0"><div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-gray-50 border-b"><tr>
            <th className="text-center px-3 py-3 text-xs font-semibold">Priority</th><th className="text-left px-4 py-3 text-xs font-semibold">Design</th>
            <th className="text-left px-4 py-3 text-xs font-semibold">Brand</th><th className="text-right px-4 py-3 text-xs font-semibold">Current</th>
            <th className="text-right px-4 py-3 text-xs font-semibold">Min Level</th><th className="text-right px-4 py-3 text-xs font-semibold">Deficit</th>
            <th className="text-center px-4 py-3 text-xs font-semibold">Action</th>
          </tr></thead>
          <tbody>{data.alerts.map((a, i) => (
            <tr key={i} className="border-b hover:bg-gray-50/80">
              <td className="px-3 py-2 text-center"><span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${priorityColor(a.priority)}`}>{a.priority.toUpperCase()}</span></td>
              <td className="px-4 py-2 font-bold">{a.design_code}</td><td className="px-4 py-2 text-xs">{a.brand}</td>
              <td className={`px-4 py-2 text-right font-bold ${a.current_stock === 0 ? 'text-red-600' : 'text-orange-600'}`}>{a.current_stock}</td>
              <td className="px-4 py-2 text-right">
                {editingMinStock === a.product_id ? (
                  <div className="flex items-center gap-1 justify-end">
                    <Input type="number" min="0" value={minStockValue} onChange={e => setMinStockValue(parseInt(e.target.value) || 0)} className="h-7 w-16 text-xs text-right" />
                    <Button size="sm" className="h-7 text-xs bg-blue-600" onClick={() => setMinStock(a.product_id)}>OK</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingMinStock(null)}>X</Button>
                  </div>
                ) : <button className="text-blue-600 hover:underline" onClick={() => { setEditingMinStock(a.product_id); setMinStockValue(a.min_stock_level); }}>{a.min_stock_level}</button>}
              </td>
              <td className="px-4 py-2 text-right font-bold text-red-600">-{a.deficit}</td>
              <td className="px-4 py-2 text-center"><Button size="sm" variant="outline" className="h-7 text-xs text-emerald-700" onClick={() => { window.location.href = '/purchase-orders'; }}>Create PO</Button></td>
            </tr>
          ))}</tbody>
        </table></div></CardContent></Card>
      )}
    </div>
  );
};

// ===== BARCODE LABELS TAB =====
const BarcodeLabelsTab = () => {
  const [entries, setEntries] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('product');
  // GRN mode
  const [labels, setLabels] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState('');
  // Product mode
  const [productSearch, setProductSearch] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [variantSelections, setVariantSelections] = useState({});
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/purchase-entries').then(r => setEntries(r.data)).catch(() => {}),
      api.get('/products').then(r => setProducts(r.data)).catch(() => {}),
      api.get('/brands').then(r => setBrands(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  // GRN mode
  const loadLabels = async () => {
    if (!selectedEntry) return;
    try { const r = await api.get(`/purchase-entries/${selectedEntry}/labels`); setLabels(r.data); } catch { toast.error('Failed to load labels'); }
  };

  const printLabels = () => {
    const win = window.open('', '_blank');
    if (!win || !labels) return;
    if (!labels.labels?.length) { toast.info('No labels to print for this entry'); win.close(); return; }
    const html = `<!DOCTYPE html><html><head><title>QR Labels - ${h(labels.grn_number)}</title>
    <style>@page{margin:5mm}body{margin:0;font-family:monospace}.grid{display:grid;grid-template-columns:repeat(3,1fr);gap:4mm}.label{border:1px solid #ccc;padding:3mm;text-align:center;break-inside:avoid}.label .design{font-size:14px;font-weight:bold}.label .sku{font-size:8px;color:#666;word-break:break-all}.label .details{font-size:11px;margin:2mm 0}.label .brand{font-size:10px;color:#444}.label img{margin:2mm auto}@media print{.no-print{display:none}}</style></head><body>
    <div class="no-print" style="padding:10px;text-align:center"><button onclick="window.print()" style="font-size:16px;padding:10px 30px;cursor:pointer">Print Labels</button> (${h(labels.total)} labels)</div>
    <div class="grid">${labels.labels.map(l => `<div class="label"><div class="brand">${h(l.brand)}</div><div class="design">${h(l.design_code)}</div><div class="details">${h(l.size)} / ${h(l.color)}</div><img src="data:image/png;base64,${h(l.qr_base64)}" width="90" height="90" /><div class="sku">${h(l.sku_code)}</div></div>`).join('')}</div></body></html>`;
    writePrintHtml(win, html);
  };

  // Product mode
  const getBrand = (id) => brands.find(b => b.id === id)?.name || '';
  const filteredProducts = productSearch.trim().length >= 1
    ? products.filter(p => p.design_code?.toLowerCase().includes(productSearch.trim().toLowerCase()) || p.category?.toLowerCase().includes(productSearch.trim().toLowerCase())).slice(0, 15)
    : [];

  const addProduct = (product) => {
    if (selectedProducts.find(p => p.id === product.id)) { toast.info('Already added'); return; }
    setSelectedProducts(prev => [...prev, product]);
    const skus = product.skus || [];
    const variants = {};
    skus.forEach(s => {
      const key = `${s.color}__${s.size}`;
      variants[key] = { color: s.color, size: s.size, qty: 1, selected: true, sku_code: s.sku_code };
    });
    setVariantSelections(prev => ({ ...prev, [product.id]: variants }));
    setProductSearch('');
  };

  const removeProduct = (productId) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== productId));
    setVariantSelections(prev => { const next = { ...prev }; delete next[productId]; return next; });
  };

  const updateVariant = (productId, key, field, value) => {
    setVariantSelections(prev => ({
      ...prev,
      [productId]: { ...prev[productId], [key]: { ...prev[productId][key], [field]: value } }
    }));
  };

  const generateProductLabels = async (format = 'pdf') => {
    const items = selectedProducts.map(p => {
      const vars = Object.values(variantSelections[p.id] || {}).filter(v => v.selected && v.qty > 0);
      return { product_id: p.id, variants: vars.map(v => ({ color: v.color, size: v.size, qty: v.qty })) };
    }).filter(i => i.variants.length > 0);
    if (items.length === 0) { toast.error('Select at least one variant with qty > 0'); return; }
    setGenerating(true);
    const endpoint = format === 'excel' ? '/generate-barcode-labels-excel' : '/generate-barcode-labels';
    try {
      const res = await api.post(endpoint, { items }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      if (format === 'excel') {
        const a = document.createElement('a'); a.href = url; a.download = 'barcode_labels.xlsx'; a.click();
      } else {
        window.open(url, '_blank');
      }
      toast.success(`Labels ${format === 'excel' ? 'downloaded' : 'generated'}!`);
    } catch { toast.error('Failed to generate'); }
    finally { setGenerating(false); }
  };

  const totalLabels = selectedProducts.reduce((sum, p) => {
    const vars = Object.values(variantSelections[p.id] || {});
    return sum + vars.filter(v => v.selected).reduce((s, v) => s + (v.qty || 0), 0);
  }, 0);

  if (loading) return <Loader color="violet" />;

  return (
    <div className="space-y-4" data-testid="barcode-labels-tab">
      {/* Mode Tabs */}
      <div className="flex gap-2">
        <Button variant={mode === 'product' ? 'default' : 'outline'} size="sm" onClick={() => setMode('product')} className={mode === 'product' ? 'bg-violet-600' : ''} data-testid="label-mode-product">
          Product Labels
        </Button>
        <Button variant={mode === 'grn' ? 'default' : 'outline'} size="sm" onClick={() => setMode('grn')} className={mode === 'grn' ? 'bg-violet-600' : ''} data-testid="label-mode-grn">
          GRN / QR Labels
        </Button>
      </div>

      {mode === 'product' ? (
        <div className="space-y-4">
          {/* Product Search */}
          <Card><CardContent className="p-4 space-y-3">
            <Label className="font-semibold text-sm">Search products to add</Label>
            <div className="relative">
              <Input value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Type design code to search..." className="h-9 text-sm" data-testid="label-product-search" />
              {filteredProducts.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredProducts.map(p => (
                    <div key={p.id} className="px-3 py-2 hover:bg-violet-50 cursor-pointer text-sm flex items-center gap-2"
                      onClick={() => addProduct(p)} data-testid={`label-add-product-${p.id}`}>
                      {p.images?.[0] && <img src={`${process.env.REACT_APP_BACKEND_URL}${p.images[0]}`} alt="" className="w-8 h-8 rounded object-cover" />}
                      <span className="font-mono font-semibold">{p.design_code}</span>
                      <Badge variant="outline" className="text-xs">{getBrand(p.brand_id)}</Badge>
                      <span className="text-gray-400 text-xs">{p.category}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent></Card>

          {/* Selected Products with Variant Pickers */}
          {selectedProducts.map(product => (
            <Card key={product.id} data-testid={`label-product-card-${product.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-sm font-mono">{product.design_code}</CardTitle>
                    <Badge variant="outline" className="text-xs">{getBrand(product.brand_id)}</Badge>
                  </div>
                  <div className="flex gap-2 items-center">
                    <button onClick={() => {
                      const vars = variantSelections[product.id] || {};
                      const allSelected = Object.values(vars).every(v => v.selected);
                      const updated = {};
                      Object.entries(vars).forEach(([k, v]) => { updated[k] = { ...v, selected: !allSelected }; });
                      setVariantSelections(prev => ({ ...prev, [product.id]: updated }));
                    }} className="text-xs text-violet-600 hover:underline" data-testid={`label-toggle-all-${product.id}`}>Toggle All</button>
                    <button onClick={() => removeProduct(product.id)} className="text-red-400 hover:text-red-600" data-testid={`label-remove-product-${product.id}`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {Object.entries(variantSelections[product.id] || {}).map(([key, v]) => (
                    <div key={key} className={`border rounded-lg p-2 text-center transition-all ${v.selected ? 'border-violet-300 bg-violet-50' : 'opacity-40 border-gray-200'}`}>
                      <input type="checkbox" checked={v.selected} onChange={e => updateVariant(product.id, key, 'selected', e.target.checked)} className="mb-1" />
                      <p className="text-xs font-medium">{v.color}</p>
                      <p className="text-xs text-gray-500">{v.size}</p>
                      <Input type="number" min="0" value={v.qty} onChange={e => updateVariant(product.id, key, 'qty', parseInt(e.target.value) || 0)}
                        className="h-6 text-xs text-center mt-1 w-16 mx-auto" disabled={!v.selected} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Generate Buttons */}
          {selectedProducts.length > 0 && (
            <div className="flex items-center justify-between p-4 bg-violet-50 border border-violet-200 rounded-lg">
              <span className="text-sm font-medium text-violet-800">{selectedProducts.length} product(s), {totalLabels} labels</span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => generateProductLabels('excel')} disabled={generating || totalLabels === 0} data-testid="label-generate-excel">
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  Excel
                </Button>
                <Button onClick={() => generateProductLabels('pdf')} disabled={generating || totalLabels === 0} className="bg-violet-600 hover:bg-violet-700" data-testid="label-generate-product">
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                  {generating ? 'Generating...' : `PDF (${totalLabels})`}
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* GRN Mode */}
          <Card><CardContent className="p-4 space-y-3">
            <Label className="font-semibold text-sm">Select Purchase Entry (GRN) to generate QR labels</Label>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <select value={selectedEntry} onChange={e => { setSelectedEntry(e.target.value); setLabels(null); }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="label-grn-select">
                  <option value="">Choose GRN</option>
                  {entries.map(e => <option key={e.id} value={e.id}>{e.grn_number} — {e.entry_date} — {e.total_qty} pcs</option>)}
                </select>
              </div>
              <Button onClick={loadLabels} disabled={!selectedEntry} className="bg-violet-600 hover:bg-violet-700" data-testid="label-generate">Generate Labels</Button>
            </div>
          </CardContent></Card>
          {labels && (
            <Card data-testid="label-preview"><CardHeader className="pb-2"><div className="flex items-center justify-between">
              <div><CardTitle className="text-base">Label Preview — {labels.grn_number}</CardTitle><CardDescription>{labels.total} labels</CardDescription></div>
              <Button onClick={printLabels} className="bg-violet-600 hover:bg-violet-700" data-testid="label-print">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                Print All ({labels.total})
              </Button>
            </div></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {labels.labels.slice(0, 24).map((l, i) => (
                  <div key={i} className="border rounded-lg p-3 text-center bg-white shadow-sm hover:shadow-md transition-shadow">
                    {l.brand && <p className="text-[10px] text-gray-500 mb-0.5">{l.brand}</p>}
                    <p className="font-bold text-sm">{l.design_code}</p><p className="text-xs text-gray-600">{l.size} / {l.color}</p>
                    {l.qr_base64 && <img src={`data:image/png;base64,${l.qr_base64}`} alt="QR" className="w-16 h-16 mx-auto mt-1" />}
                    <p className="text-[9px] font-mono text-gray-400 truncate mt-0.5">{l.sku_code}</p>
                  </div>
                ))}
                {labels.total > 24 && <div className="border rounded-lg p-3 text-center bg-gray-50 flex items-center justify-center"><p className="text-xs text-gray-500">+{labels.total - 24} more</p></div>}
              </div>
            </CardContent></Card>
          )}
        </div>
      )}
    </div>
  );
};

export default OperationsHub;
